import {
  ClipboardTextIcon,
  CoffeeIcon,
  type IconProps,
  LightningIcon,
  NewspaperIcon,
  TrendUpIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import HandraiseGradientLogo from "@/assets/svg/Handraise_gradient.svg?react";
import { PromptCard } from "@/components/ui/card/card";
import HalloweenAnimation from "@/components/ui/chat/HalloweenAnimation";
import { HOLIDAY_THEME } from "@/lib/constants/holiday-theme";
import { cn } from "@/lib/utils";

export interface ChatEmptyStateSuggestion {
  label: string;
  prompt: string;
  icon?: React.ReactElement<IconProps>;
}

export interface ChatEmptyStateProps {
  /** Called when the user taps a suggestion chip or otherwise submits a prompt. */
  onSubmit: (text: string) => void;
  /** Suggestion chips (label + prompt). Defaults to news-intelligence prompts (e.g. morning briefing, Share of Voice). */
  suggestions?: ChatEmptyStateSuggestion[];
  /** Rendered between the headline and the prompt cards (e.g. ChatInput). */
  children?: React.ReactNode;
}

/** Default prompt cards: 8 tiles in a 4×2 grid. */
const DEFAULT_SUGGESTIONS: ChatEmptyStateSuggestion[] = [
  {
    label: "Morning Briefing",
    prompt: "Summarize today’s key narratives.",
    icon: <CoffeeIcon />,
  },
  {
    label: "LLM Impact",
    prompt: "How do AI models describe our brand?",
    icon: <LightningIcon />,
  },
  {
    label: "Share of Voice",
    prompt: "Compare our share of voice with competitors.",
    icon: <TrendUpIcon />,
  },
  {
    label: "Reputation Risks",
    prompt: "Identify emerging reputational risks.",
    icon: <WarningIcon />,
  },
  {
    label: "Competitor Intel",
    prompt: "What narratives are driving competitor coverage?",
    icon: <ClipboardTextIcon />,
  },
  {
    label: "Executive Brief",
    prompt: "Summarize this week’s most important coverage.",
    icon: <NewspaperIcon />,
  },
  {
    label: "Narrative Trends",
    prompt: "Which narratives are gaining or losing momentum?",
    icon: <TrendUpIcon />,
  },
  {
    label: "Story Impact",
    prompt: "Which stories are gaining the most traction?",
    icon: <LightningIcon />,
  },
];

const isHalloween = HOLIDAY_THEME === "halloween";

const ChatEmptyState = ({
  onSubmit,
  suggestions = DEFAULT_SUGGESTIONS,
  children,
}: ChatEmptyStateProps) => (
  <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-3 py-6 sm:px-6 sm:py-12">
    <div className="flex w-full flex-col items-center">
      <div
        className={cn(
          "mb-[44px] flex w-full flex-col items-center",
          isHalloween ? "gap-2" : "gap-6",
        )}
      >
        {isHalloween ? (
          <>
            <link
              rel="stylesheet"
              href="https://fonts.googleapis.com/css2?family=Grenze+Gotisch&display=swap"
            />
            <HalloweenAnimation />
          </>
        ) : (
          <HandraiseGradientLogo
            className="h-auto w-full max-w-[131px] shrink-0"
            role="img"
            aria-label="Handraise"
          />
        )}
        <div className="w-full text-center">
          <p
            className={
              isHalloween
                ? "font-['Grenze_Gotisch',serif] text-[40px] text-black"
                : "font-serif text-[32px] text-violet-950"
            }
          >
            What’s on your mind today?
          </p>
        </div>
      </div>

      {children}

      <div className="mt-4 w-full sm:mt-16">
        <p className="pb-3 text-sm leading-4 text-gray-500">
          Start with an idea:
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {suggestions.map((suggestion) => (
            <PromptCard
              key={suggestion.label}
              variant="mono"
              prompt={suggestion.prompt}
              label={suggestion.label}
              className="min-w-0"
              onClick={() => onSubmit(suggestion.prompt)}
              icon={suggestion.icon}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default ChatEmptyState;
